import { useState } from 'react';
import { StarRating, PlatformIcon, sampleReviews, mockTotalReviews, mockAverageRating, getFontFamily } from './SharedComponents';
import ReviewsModal from './ReviewsModal';

// Enhanced Badge Preview with new design
export const BadgePreview = ({ reviews = [], previewMode = 'desktop', selectedPlatforms = [], locationData = [], widgetConfig = {} }) => {
    const [showReviewsModal, setShowReviewsModal] = useState(false);

    // Use selected platforms or fallback to sample data platforms
    const displayReviews = (reviews.length > 0 && selectedPlatforms.length > 0) ? reviews : sampleReviews;

    // Extract unique platforms with their icon URLs
    const platformsMap = new Map();
    displayReviews.forEach(review => {
        const platformName = review.platform?.name || review.platform;
        const iconUrl = review.platform?.iconLink;
        if (platformName && !platformsMap.has(platformName)) {
            platformsMap.set(platformName, { name: platformName, iconUrl });
        }
    });
    const uniquePlatforms = Array.from(platformsMap.values()).slice(0, 3);

    // Calculate average rating and total count (use mock data for demo)
    const totalReviews = mockTotalReviews; // Use mock 41,300 for demo
    const averageRating = mockAverageRating; // Use mock 4.8 for demo
    const reviewCount = totalReviews > 1000 ? `${Math.floor(totalReviews / 1000)}k` : totalReviews.toLocaleString();

    // Generate container styles based on widgetConfig
    const getContainerStyles = () => {
        const backgroundConfig = widgetConfig.style?.background || {};
        const borderRadius = backgroundConfig.borderRadius ?? 16;
        const shadow = backgroundConfig.shadow ?? true;
        const colorScheme = widgetConfig.style?.colorScheme || 'light';

        return {
            borderRadius: `${borderRadius}px`,
            boxShadow: shadow ? '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)' : 'none',
            backgroundColor: backgroundConfig.color || (colorScheme === 'dark' ? '#1F2937' : '#FFFFFF'),
            fontFamily: getFontFamily(widgetConfig.style?.fontFamily || 'default')
        };
    };

    const colorScheme = widgetConfig.style?.colorScheme || 'light';

    return (
        <>
            <div
                className={`border text-center ${previewMode === 'mobile' ? 'p-4 max-w-xs' : 'p-6 max-w-sm'} mx-auto ${colorScheme === 'dark' ? 'border-gray-600' : 'border-gray-200'}`}
                style={getContainerStyles()}
            >
                {/* Platform Icons at the top */}
                <div className="flex justify-center items-center space-x-2 mb-4">
                    {uniquePlatforms.slice(0, 3).map((platformData, index) => (
                        <div key={index} className="relative">
                            <PlatformIcon
                                platform={platformData.name}
                                iconUrl={platformData.iconUrl}
                                className={previewMode === 'mobile' ? 'w-8 h-8' : 'w-10 h-10'}
                            />
                        </div>
                    ))}
                    {uniquePlatforms.length > 3 && (
                        <div className={`rounded-full flex items-center justify-center ${previewMode === 'mobile' ? 'w-8 h-8' : 'w-10 h-10'} ${colorScheme === 'dark' ? 'bg-gray-600' : 'bg-gray-200'}`}>
                            <span className={`text-xs font-medium ${colorScheme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>...</span>
                        </div>
                    )}
                </div>

                {/* Overall Rating Heading */}
                <h3 className={`font-bold mb-3 ${previewMode === 'mobile' ? 'text-lg' : 'text-xl'}`} style={{ color: colorScheme === 'dark' ? '#FFFFFF' : '#111827' }}>
                    Overall Rating
                </h3>

                {/* Large Rating Number */}
                <div className={`font-bold mb-2 ${previewMode === 'mobile' ? 'text-3xl' : 'text-4xl'}`} style={{ color: colorScheme === 'dark' ? '#FFFFFF' : '#111827' }}>
                    {averageRating.toFixed(1)}
                </div>

                {/* Star Rating */}
                <div className="flex justify-center mb-4">
                    <StarRating
                        rating={Math.round(averageRating)}
                        size={previewMode === 'mobile' ? 'w-5 h-5' : 'w-6 h-6'}
                        color={widgetConfig.style?.rating?.starsColor || '#F1C40F'}
                    />
                </div>

                {/* Review Link */}
                <button
                    onClick={() => setShowReviewsModal(true)}
                    className={`hover:opacity-80 underline transition-colors ${previewMode === 'mobile' ? 'text-sm' : 'text-base'}`}
                    style={{ color: widgetConfig.style?.accentColor || '#4A90E2' }}
                >
                    Read our {reviewCount} reviews
                </button>
            </div>

            {/* Reviews Modal */}
            <ReviewsModal
                isOpen={showReviewsModal}
                onClose={() => setShowReviewsModal(false)}
                reviews={displayReviews}
                averageRating={averageRating}
                totalReviews={totalReviews}
                uniquePlatforms={uniquePlatforms}
                locationData={locationData}
                accentColor={widgetConfig.style?.accentColor || '#4A90E2'}
            />
        </>
    );
};

// Floating Badge Preview - Same as Badge but positioned as overlay
export const FloatingBadgePreview = ({ reviews = [], previewMode = 'desktop', selectedPlatforms = [], locationData = [], position = 'bottom-left', widgetConfig = {} }) => {
    const [showReviewsModal, setShowReviewsModal] = useState(false);

    // Use selected platforms or fallback to sample data platforms
    const displayReviews = (reviews.length > 0 && selectedPlatforms.length > 0) ? reviews : sampleReviews;

    // Extract unique platforms with their icon URLs
    const platformsMap = new Map();
    displayReviews.forEach(review => {
        const platformName = review.platform?.name || review.platform;
        const iconUrl = review.platform?.iconLink;
        if (platformName && !platformsMap.has(platformName)) {
            platformsMap.set(platformName, { name: platformName, iconUrl });
        }
    });
    const uniquePlatforms = Array.from(platformsMap.values()).slice(0, 3);

    // Calculate average rating and total count (use mock data for demo)
    const totalReviews = mockTotalReviews; // Use mock 41,300 for demo
    const averageRating = mockAverageRating; // Use mock 4.8 for demo
    const reviewCount = totalReviews > 1000 ? `${Math.floor(totalReviews / 1000)}k` : totalReviews.toLocaleString();

    // Generate container styles based on widgetConfig
    const getContainerStyles = () => {
        const backgroundConfig = widgetConfig.style?.background || {};
        const borderRadius = backgroundConfig.borderRadius ?? 16;
        const shadow = backgroundConfig.shadow ?? true;
        const colorScheme = widgetConfig.style?.colorScheme || 'light';

        return {
            borderRadius: `${borderRadius}px`,
            boxShadow: shadow ? '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)' : 'none',
            backgroundColor: backgroundConfig.color || (colorScheme === 'dark' ? '#1F2937' : '#FFFFFF'),
            fontFamily: getFontFamily(widgetConfig.style?.fontFamily || 'default')
        };
    };

    const colorScheme = widgetConfig.style?.colorScheme || 'light';

    // Position classes based on position prop
    const getPositionClasses = () => {
        const baseClasses = 'fixed z-50';
        const mobileSpacing = previewMode === 'mobile' ? 'bottom-4' : 'bottom-6';
        const desktopSpacing = previewMode === 'mobile' ? 'bottom-4' : 'bottom-6';

        switch (position) {
            case 'bottom-left':
                return `${baseClasses} ${mobileSpacing} ${previewMode === 'mobile' ? 'left-4' : 'left-6'}`;
            case 'bottom-right':
            default:
                return `${baseClasses} ${desktopSpacing} ${previewMode === 'mobile' ? 'right-4' : 'right-6'}`;
        }
    };

    return (
        <>
            {/* Demo website content background */}
            <div className={`relative min-h-screen ${colorScheme === 'dark' ? 'bg-gray-900' : 'bg-gray-50'}`}>
                <div className={`rounded-lg flex items-center justify-center ${previewMode === 'mobile' ? 'p-6 min-h-[400px]' : 'p-8 min-h-[500px]'} mx-4 my-8 ${colorScheme === 'dark' ? 'bg-gray-800' : 'bg-white'}`}>
                    <div className="text-center">
                        <h2 className={`font-semibold mb-4 ${previewMode === 'mobile' ? 'text-lg' : 'text-xl'}`} style={{ color: colorScheme === 'dark' ? '#D1D5DB' : '#374151' }}>
                            Your Website Content
                        </h2>
                        <p className={`${previewMode === 'mobile' ? 'text-sm' : ''} max-w-md`} style={{ color: colorScheme === 'dark' ? '#9CA3AF' : '#6B7280' }}>
                            This represents your website content. The floating badge will appear as an overlay on top of your actual website.
                        </p>
                    </div>
                </div>

                {/* Floating Badge - Exact same design as BadgePreview */}
                <div className={getPositionClasses()}>
                    <div
                        className={`border text-center transition-all duration-300 hover:shadow-2xl ${previewMode === 'mobile' ? 'p-4 max-w-xs' : 'p-6 max-w-sm'} ${colorScheme === 'dark' ? 'border-gray-600' : 'border-gray-200'}`}
                        style={getContainerStyles()}
                    >
                        {/* Platform Icons at the top */}
                        <div className="flex justify-center items-center space-x-2 mb-4">
                            {uniquePlatforms.slice(0, 3).map((platformData, index) => (
                                <div key={index} className="relative">
                                    <PlatformIcon
                                        platform={platformData.name}
                                        iconUrl={platformData.iconUrl}
                                        className={previewMode === 'mobile' ? 'w-8 h-8' : 'w-10 h-10'}
                                    />
                                </div>
                            ))}
                            {uniquePlatforms.length > 3 && (
                                <div className={`rounded-full flex items-center justify-center ${previewMode === 'mobile' ? 'w-8 h-8' : 'w-10 h-10'} ${colorScheme === 'dark' ? 'bg-gray-600' : 'bg-gray-200'}`}>
                                    <span className={`text-xs font-medium ${colorScheme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>...</span>
                                </div>
                            )}
                        </div>

                        {/* Overall Rating Heading */}
                        <h3 className={`font-bold mb-3 ${previewMode === 'mobile' ? 'text-lg' : 'text-xl'}`} style={{ color: colorScheme === 'dark' ? '#FFFFFF' : '#111827' }}>
                            Overall Rating
                        </h3>

                        {/* Large Rating Number */}
                        <div className={`font-bold mb-2 ${previewMode === 'mobile' ? 'text-3xl' : 'text-4xl'}`} style={{ color: colorScheme === 'dark' ? '#FFFFFF' : '#111827' }}>
                            {averageRating.toFixed(1)}
                        </div>

                        {/* Star Rating */}
                        <div className="flex justify-center mb-4">
                            <StarRating
                                rating={Math.round(averageRating)}
                                size={previewMode === 'mobile' ? 'w-5 h-5' : 'w-6 h-6'}
                                color={widgetConfig.style?.rating?.starsColor || '#F1C40F'}
                            />
                        </div>

                        {/* Review Link */}
                        <button
                            onClick={() => setShowReviewsModal(true)}
                            className={`hover:opacity-80 underline transition-colors ${previewMode === 'mobile' ? 'text-sm' : 'text-base'}`}
                            style={{ color: widgetConfig.style?.accentColor || '#4A90E2' }}
                        >
                            Read our {reviewCount} reviews
                        </button>
                    </div>
                </div>
            </div>

            {/* Reviews Modal */}
            <ReviewsModal
                isOpen={showReviewsModal}
                onClose={() => setShowReviewsModal(false)}
                reviews={displayReviews}
                averageRating={averageRating}
                totalReviews={totalReviews}
                uniquePlatforms={uniquePlatforms}
                locationData={locationData}
                accentColor={widgetConfig.style?.accentColor || '#4A90E2'}
            />
        </>
    );
}; 